"use client";

import { useEffect, useId, useRef, useState } from "react";
import { Icon } from "@/shared/ui";

export type AccountMenuCopy = {
  menu: string;
  signedInAs: string;
  cabinet: string;
  signOut: string;
};

export type AccountMenuProps = {
  copy: AccountMenuCopy;
  displayName: string;
  email: string;
  /** The cabinet address in the page's language. */
  cabinetHref: string;
  /** The page's locale, sent with sign-out so the homepage opens in it. */
  locale: string;
  /** The mobile-menu form: the account and its actions as one block, no popover. */
  wide?: boolean;
};

function initials(name: string): string {
  const letters = name.trim().split(/\s+/).map((word) => Array.from(word)[0] ?? "").join("");
  return Array.from(letters).slice(0, 2).join("").toUpperCase();
}

function SignOutForm({ locale, label, className }: { locale: string; label: string; className: string }) {
  return (
    <form method="post" action="/api/access/sign-out" className="acct-out">
      <input type="hidden" name="locale" value={locale} />
      <button className={className} type="submit" data-closes-menu="">
        <Icon name="arrow" className="ic acct-out-ic" />{label}
      </button>
    </form>
  );
}

/**
 * The signed-in staff member's menu in the header: who is signed in, the
 * cabinet and sign-out (a plain form POST, so it works without scripts too).
 */
export function AccountMenu({ copy, displayName, email, cabinetHref, locale, wide = false }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    const onClick = (event: globalThis.MouseEvent) => {
      if (root.current && event.target instanceof Node && !root.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
    };
  }, [open]);

  if (wide) {
    return (
      <div className="acct-wide">
        <div className="acct-who">
          <span className="acct-avatar" aria-hidden="true">{initials(displayName)}</span>
          <span className="acct-id"><span className="acct-label">{copy.signedInAs}</span><span className="acct-name">{displayName}</span></span>
        </div>
        <a className="btn btn-primary" href={cabinetHref} data-closes-menu="">{copy.cabinet}</a>
        <SignOutForm locale={locale} label={copy.signOut} className="btn btn-ghost" />
      </div>
    );
  }

  return (
    <div className="acct login-open" ref={root}>
      <button
        className="acct-btn"
        type="button"
        ref={trigger}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`${copy.menu}: ${displayName}`}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="acct-avatar" aria-hidden="true">{initials(displayName)}</span>
        <span className="acct-btn-name">{displayName}</span>
      </button>
      <div className="acct-panel" id={panelId} hidden={!open}>
        <div className="acct-id">
          <span className="acct-label">{copy.signedInAs}</span>
          <span className="acct-name">{displayName}</span>
          <span className="acct-email">{email}</span>
        </div>
        <a className="acct-item" href={cabinetHref}><Icon name="list" className="ic" />{copy.cabinet}</a>
        <SignOutForm locale={locale} label={copy.signOut} className="acct-item" />
      </div>
    </div>
  );
}
